import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './StripePages.css';
import Swal from 'sweetalert2';
import {
  ensureFirebaseApp,
  getPathWithoutReturnToken,
  getReturnToken,
} from '../utils/mobileWebAuth';

function PlansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [user, setUser] = useState(null);
  const source = searchParams.get('source') === 'mobile' ? 'mobile' : 'web';
  const appReturnUrl = searchParams.get('app_return') || 'fodyfee://stripe/return';
  const returnToken = getReturnToken(searchParams);
  const canUseMobileGuestFlow = source === 'mobile' && !!returnToken;
  const mobileQuery = `source=mobile&app_return=${encodeURIComponent(appReturnUrl)}`;
  const otpPath = source === 'mobile' ? `/admin/signup/otp?${mobileQuery}` : '/admin/signup/otp';

  useEffect(() => {
    ensureFirebaseApp();
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        loadUserData({ uid: currentUser.uid });
        loadSubscriptions();
      } else if (canUseMobileGuestFlow) {
        // No Firebase session in this external mobile browser — identify via
        // the signed return token instead of trusting a raw userId. `uid` is
        // unknown here on purpose; every backend call below uses returnToken.
        setUser({ isMobileGuest: true });
        loadUserData({ returnToken });
        loadSubscriptions();
      } else {
        localStorage.setItem('redirectAfterLogin', getPathWithoutReturnToken(searchParams));
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, source, appReturnUrl, returnToken, canUseMobileGuestFlow]);

  const loadUserData = async ({ uid, returnToken: token }) => {
    try {
      let otpVerified;
      let otpRequired;
      let hasStripeAccount;
      let subStatus;
      let subName;
      let subEndDate;

      if (uid) {
        // Real Firebase session — read directly, as before.
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', uid));
        const userData = userDoc.data();
        otpVerified = userData?.signupOtpVerified;
        otpRequired = userData?.signupOtpRequired === true || otpVerified === false;
        hasStripeAccount = !!userData?.stripeAccountId;
        subStatus = userData?.activeSubscriptionStatus;
        subName = userData?.activeSubscriptionName;
        subEndDate = userData?.activeSubscriptionEndDate;
      } else {
        // No session in this browser — go through the backend instead of an
        // unauthenticated client-side Firestore read (which security rules
        // would reject anyway). getStripeAccountStatus verifies `returnToken`
        // server-side and returns this same profile data.
        const functions = getFunctions();
        const getStripeAccountStatus = httpsCallable(functions, 'getStripeAccountStatus');
        const result = await getStripeAccountStatus({ returnToken: token });
        const data = result.data || {};
        otpVerified = data.otpVerified;
        otpRequired = data.otpRequired;
        hasStripeAccount = !!data.connected;
        subStatus = data.activeSubscriptionStatus;
        subName = data.activeSubscriptionName;
        subEndDate = data.activeSubscriptionEndDate;
      }

      if (otpRequired && otpVerified !== true) {
        navigate(otpPath);
        return;
      }

      // Check if stripe account exists.  Don't force-redirect to onboarding
      // based on cached data — the return page already confirmed completion.
      if (!hasStripeAccount) {
        navigate(source === 'mobile' ? `/admin/onboarding?${mobileQuery}` : '/admin/onboarding');
        return;
      }

      // Check existing subscription
      if (subStatus === 'active') {
        setActiveSubscription({ name: subName, endDate: subEndDate });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const functions = getFunctions();
      const getSubscriptionProducts = httpsCallable(functions, 'getSubscriptionProducts');
      const result = await getSubscriptionProducts();
      const data = result.data;

      if (data.success && data.products) {
        // Filter and sort
        const filtered = data.products
          .filter(p => p.metadata && p.metadata.platform === 'fodyfee')
          .sort((a, b) => {
            const posA = parseInt(a.metadata?.position || '999', 10);
            const posB = parseInt(b.metadata?.position || '999', 10);
            return posA - posB;
          });
        setSubscriptions(filtered);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      Swal.fire('Error', 'Failed to load subscription plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (subscription) => {
    if (!user) return;

    const prices = subscription.prices || [];
    // Pick the RECURRING price — a product can carry stray one-time prices
    // (e.g. created by mistake via API) and subscription-mode Checkout
    // hard-rejects them ("must provide at least one recurring price").
    // Stripe lists newest first, so prices[0] is NOT safe to trust.
    const recurringPrice = prices.find(p => p.recurring != null);
    const priceId = (recurringPrice ?? prices[0])?.id ?? null;

    if (!priceId) {
      Swal.fire('Error', 'No price found for this subscription', 'error');
      return;
    }

    try {
      setLoading(true);
      const functions = getFunctions();
      const createSubscriptionCheckout = httpsCallable(functions, 'createSubscriptionCheckout');
      const result = await createSubscriptionCheckout({
        priceId: priceId,
        productName: subscription.name,
        source,
        appReturnUrl,
        // Only meaningful (and only sent) when there's no real Firebase
        // session in this browser — the backend prefers request.auth when
        // present and falls back to this signed token otherwise.
        ...(user?.isMobileGuest ? { returnToken } : {}),
      });
      const data = result.data;

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      Swal.fire('Error', error.message || 'Failed to create checkout session', 'error');
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return { amount: '', interval: '' };
    const amount = price.unitAmount / 100;
    const interval = price.interval ? `/${price.interval}` : '';
    return { amount: `$${amount.toFixed(2)}`, interval };
  };

  const cleanText = (text) => {
    if (!text) return '';
    return text.replace(/\*/g, '').trim();
  };

  const parseDescription = (description) => {
    if (!description) return { title: '', features: [] };
    const parts = description.split('^');
    const title = cleanText(parts[0]) || '';
    const features = parts.slice(1).map(cleanText).filter(Boolean);
    return { title, features };
  };

  if (loading) {
    return (
      <div className="stripe-page loading">
        <div className="stripe-card">
          <div className="loading-spinner"></div>
          <h1>Loading Plans</h1>
          <p>Please wait while we fetch available subscription plans...</p>
        </div>
      </div>
    );
  }

  const floatingIngredients = (
    <div className="ff-hero-ingredients">
      <div className="ff-ingredient tomato"></div>
      <div className="ff-ingredient pepper"></div>
      <div className="ff-ingredient mushroom"></div>
      <div className="ff-ingredient leaf"></div>
      <div className="ff-ingredient onion"></div>
      <div className="ff-ingredient carrot"></div>
      <div className="ff-ingredient garlic"></div>
      <div className="ff-ingredient broccoli"></div>
      <div className="ff-ingredient banana"></div>
      <div className="ff-ingredient bread"></div>
      <div className="ff-ingredient egg"></div>
      <div className="ff-ingredient sausage"></div>
      <div className="ff-ingredient cheese"></div>
      <div className="ff-ingredient lemon"></div>
      <div className="ff-ingredient strawberry"></div>
    </div>
  );

  return (
    <div className="stripe-page plans" style={{ position: 'relative', overflow: 'hidden' }}>
      {floatingIngredients}
      <div className="plans-header">
        <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '60px', height: '60px' }} />
        <h1>Choose Your Plan</h1>
        <p>Select the plan that best fits your business needs</p>
      </div>

      {activeSubscription && (
        <div className="active-subscription-banner">
          <div className="success-icon">✓</div>
          <div>
            <h3>You are currently subscribed to {activeSubscription.name}</h3>
            <p>Your subscription renews on {new Date(activeSubscription.endDate).toLocaleDateString()}</p>
          </div>
          <button onClick={() => navigate('/admin/dashboard')} className="primary-button">
            Go to Dashboard
          </button>
        </div>
      )}

      <div className="plans-grid">
        {subscriptions.length === 0 ? (
          <div className="no-plans">
            <p>No subscription plans available at this time.</p>
            <button onClick={() => navigate('/admin/dashboard')} className="primary-button">
              Go to Dashboard
            </button>
          </div>
        ) : (
          subscriptions.map((sub) => {
            const { title, features } = parseDescription(sub.description);
            const price = sub.prices?.[0];

            return (
              <div key={sub.id} className="plan-card">
                <div className="plan-header">
                  <h2>{sub.name}</h2>
                  {title && <p className="plan-subtitle">{title}</p>}
                </div>
                <div className="plan-price">
                  {(() => {
                    const { amount, interval } = formatPrice(price);
                    return (
                      <span className="price">
                        {amount}
                        {interval && (
                          <span style={{ color: '#9ca3af', fontSize: '18px', fontWeight: 400, marginLeft: '4px' }}>
                            {interval}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                <ul className="plan-features">
                  {features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="feature-check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className="subscribe-button"
                  onClick={() => handleSubscribe(sub)}
                  disabled={loading || (activeSubscription?.name === sub.name)}
                >
                  {activeSubscription?.name === sub.name ? 'Current Plan' : 'Subscribe'}
                </button>
              </div>
            );
          })
        )}
      </div>

      <div className="plans-footer">
        <p className="text-muted">
          All plans include secure payment processing powered by Stripe.
          Cancel anytime from your account settings.
        </p>
      </div>
    </div>
  );
}

export default PlansPage;
